import { useEffect, useState } from 'react'

import emitter from 'scripts/utils/emitter'

import type { Product } from 'catalog/pages/NewProduct/types'

import { ButtonsSection, PriceTag, SellerLabel, ServiceLabel } from './'
import * as S from './styled'

interface BuyBoxProps {
  isAssistedSale: boolean
  isOnDemand: boolean
  isOfferSchemaEnabled: boolean
  shouldRenderFreeShippingBadge: boolean
  shopName: string
  brandedInstallments: {
    amount: number
    value: string
  }
  installments: {
    amount: number
    value: string
  }
  discountPercentage: number
  discountType: string
  paymentMethodsDiscount: string
  price: {
    from?: {
      integers: string
      decimals: string
    }
    region?: {
      integers: string
      decimals: string
    }
    to: {
      integers: string
      decimals: string
    }
  }
  product: {
    brand: string
    hidePrice: boolean
    isSoldOut: boolean
    id: number
    img: string
    isFreeShipping: boolean
    isOutlet: boolean
    name: string
    unit: string
    hasCrossServices: boolean
  }
  packaging: {
    unit: string
    quantity: string
    label: string
    price: {
      integers: string
      decimals: string
    }
  }
  purchaseButtons: {
    ecommerce: boolean
    pickupInStore: boolean
    assistedSale: boolean
  }
  regionId: string
}

const BuyBox = ({
  isAssistedSale,
  isOnDemand,
  isOfferSchemaEnabled,
  shouldRenderFreeShippingBadge,
  shopName,
  brandedInstallments,
  installments,
  discountPercentage,
  discountType,
  paymentMethodsDiscount,
  price,
  product,
  packaging,
  purchaseButtons,
  regionId
}: BuyBoxProps) => {
  const [productPackingCount, setProductPackagingCount] = useState(0)
  const [sellerInfo, setSellerInfo] = useState({
    shopOfferId: '',
    shopName: shopName
  })

  const getPackagingCount = (
    cartProducts: Array<Product>,
    selectedProductId: number
  ): number => {
    const cartProduct = cartProducts.find(
      item => item.productId === selectedProductId
    )

    const hasPackagingCount = cartProduct && cartProduct.packagingCount

    return hasPackagingCount ? cartProduct.packagingCount : 0
  }

  useEffect(() => {
    emitter.on('cart:products-added', cart => {
      setProductPackagingCount(
        getPackagingCount(cart.shippings[0].items, product.id)
      )
    })

    emitter.on('cart:products-replace', cart => {
      if (cart) {
        setProductPackagingCount(
          getPackagingCount(cart.shippings[0].items, product.id)
        )
      }
    })

    emitter.on('cart:removed', cart => {
      setProductPackagingCount(cart.uniqueProductsCount)
    })

    emitter.on('buybox:sellerChange', sellerInfo => {
      setSellerInfo({
        shopOfferId: sellerInfo.shopOfferId,
        shopName: sellerInfo.shopName
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <S.Wrapper>
      <PriceTag
        isAssistedSale={isAssistedSale}
        isOnDemand={isOnDemand}
        isOfferSchemaEnabled={isOfferSchemaEnabled}
        isOutlet={product.isOutlet}
        isSoldOut={product.isSoldOut}
        hidePrice={product.hidePrice}
        brandedInstallmentsAmount={brandedInstallments.amount}
        installments={installments}
        discountPercentage={discountPercentage}
        discountType={discountType}
        paymentMethodsDiscount={paymentMethodsDiscount}
        unit={product.unit}
        price={price}
        packaging={packaging}
      />

      <ButtonsSection
        purchaseButtons={purchaseButtons}
        productId={product.id}
        regionId={regionId}
        productPackingCount={productPackingCount}
        skuId={sellerInfo.shopOfferId}
      />

      <ServiceLabel productId={product.id} regionId={regionId} />

      <SellerLabel shopName={sellerInfo.shopName} />
    </S.Wrapper>
  )
}

export default BuyBox
